<template>
  <ACard
    class="sec-list"
    @keydown="handleKeyDown"
    tabindex="0"
  >
    <template #title>
      <h5 class="sec-list__h">{{ $t('label.43') }}</h5>
    </template>

    <template #action>
      <ABtn kind="ghost" icon="link" @click.prevent="mappingOpen = true">맵핑 관리</ABtn>
    </template>

    <p class="sec-list__sub">{{ $t('txt.19') }}</p>

    <VeeForm
      ref="formRef"
      :validation-schema="schema"
      @submit="onSubmit"
    >
      <div class="sec-list__tabs">
        <TabNav
          v-model="activeTab"
          :tabs="[
            { id: 'book', label: 'Book Layer', icon: 'menu_book' },
            { id: 'popup', label: 'Popup Layer', icon: 'assignment' }
          ]"
        />
      </div>

      <!-- Book Layer: ScssEditor 전체 화면 -->
      <div v-if="activeTab === 'book'" class="sec-list__book">
        <ScssEditor :barcode="$route.query.barcode" />
      </div>

      <!-- Popup Layer -->
      <div v-else class="sec-list__layout">
        <!-- 좌측 -->
        <div class="sec-list__left">
          <Field v-slot="{ field }" name="chapters">
            <div class="p-field">
              <label class="p-field__label">Chapters</label>
              <CustomSelect
                :options="chapterOptions"
                :model-value="field.value"
                placeholder="Select Chapter"
                @update:model-value="chapertNumberChange"
                @blur="field.onBlur"
              />
            </div>
          </Field>

          <div class="sec-list__list-wrap">
            <List
              :items-config="itemsConfig"
              :events="tasks"
              :chapter-number="chapterNumber"
              @update:events="sectionReSorting"
              @view="loadSection"
              @delete="deleteSection"
              @guide-book="openGuideBookModal"
              @audio-url="openAudioUrlModal"
              @memo="openMemoModal"
            />
          </div>

          <div v-if="chapterNumber != null" class="sec-list__add-row">
            <ABtn kind="yellow" icon="add" @click.prevent="addSection">새 섹션</ABtn>
          </div>
        </div>

        <!-- 우측 -->
        <div class="sec-list__right">
          <PopupLayerSection
            v-model="currentSection"
            :sections="tasks"
            :chapter-number="chapterNumber"
          />
        </div>
      </div>

      <div v-show="activeTab === 'popup'" class="sec-list__actions">
        <ABtn type="submit" kind="yellow" icon="save">{{ $t("btn.04") }}</ABtn>
      </div>
    </VeeForm>

    <!-- Guide Book 모달 -->
    <GuideBookModal
      :is-visible="showGuideBookModal"
      :guide-book="currentSection?.guideBook || []"
      :section-data="currentSection"
      @close="closeGuideBookModal"
      @save="saveGuideBook"
    />

    <!-- Audio URL 모달 -->
    <AudioUrlModal
      :is-visible="showAudioUrlModal"
      :audio-url="currentSection?.content?.audioUrl || ''"
      :section-data="currentSection"
      :barcode="$route.query.barcode"
      @close="closeAudioUrlModal"
      @save="saveAudioUrl"
    />

    <!-- 섹션 메모 모달 -->
    <SectionMemoModal
      :is-visible="showMemoModal"
      :memo="memoTarget?.memo"
      :unique-key="memoTarget?.uniqueKey"
      :saving="memoSaving"
      @close="closeMemoModal"
      @save="saveMemo"
    />

    <!-- data-id 맵핑 관리 모달 -->
    <TextbookMappingModal
      v-if="$route.query.barcode"
      v-model="mappingOpen"
      :barcode="$route.query.barcode"
    />
  </ACard>
</template>

<script>
import ScssEditor from "@/components/ScssEditor.vue";
import PopupLayerSection from "@/components/PopupLayerSection.vue";
import GuideBookModal from "@/components/GuideBookModal.vue";
import AudioUrlModal from "@/components/AudioUrlModal.vue";
import SectionMemoModal from "@/components/SectionMemoModal.vue";
import eventBus from "@/eventBus";

import List from "@/components/List.vue";
import CustomSelect from "@/components/CustomSelect.vue";
import { Field, Form as VeeForm } from "vee-validate";
import * as yup from "yup";
import TabNav from "@/components/TabNav.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import TextbookMappingModal from "@/components/TextbookMappingModal.vue";

export default {
  name: "SectionsList",
  components: {
    ScssEditor, PopupLayerSection,
    GuideBookModal, AudioUrlModal, SectionMemoModal,
    List, CustomSelect,
    Field, VeeForm,
    TabNav,
    ACard, ABtn, TextbookMappingModal,
  },
  data() {
    return {
      chapterNumber: null,
      sectionNumber: null,
      uniqueKey: null,
      sectionType: "text",
      chapterOptions: [],
      currentSection: this.getDefaultSection("text", "book"),
      sections: [],
      tasks: [],
      schema: yup.object({}),
      activeTab: "book",
      showGuideBookModal: false,
      showAudioUrlModal: false,
      showMemoModal: false,
      memoTarget: null,
      memoSaving: false,
      mappingOpen: false,
    };
  },
  computed: {
    currentComponent() {
      if (this.activeTab === "book") return "BookLayerSection";
      return "PopupLayerSection";
    },
    itemsConfig() {
      return {
        title: this.activeTab === "book" ? "Book Layer Sections" : "Popup Layer Sections",
        label: "Unique Key",
      };
    },
    sectionTypes() {
      if (this.activeTab === "book") {
        return [
          { value: "text", label: "Text" },
          { value: "practice", label: "Practice" },
        ];
      }
      return [{ value: "practice", label: "Practice" }];
    },
  },
  watch: {
    activeTab(newValue) {
      if (newValue === "popup") {
        this.sectionType = "practice";
        this.currentSection = this.getDefaultSection("practice", "popup");
      } else {
        this.sectionType = "text";
        this.currentSection = this.getDefaultSection("text", "book");
      }
    },
  },
  mounted() {
    if (!this.$route.query.barcode) {
      this.$router.push({ path: "textbooks" });
    }
    this.getChapters();
    eventBus.on("reloadChapters", this.getChapters);
  },
  beforeUnmount() {
    eventBus.off("reloadChapters", this.getChapters);
  },
  updated() {
    this.getChapters();
  },
  methods: {
    async chapertNumberChange(value) {
      const barcode = this.$route.query.barcode;
      this.chapterNumber = value;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/textbookContents/${barcode}/${this.chapterNumber}`,
          loadingBar: true,
        });
        if (res.status === 200 && Array.isArray(res.data)) {
          this.sections = res.data
            .filter((item) => item.layer_type === "book")
            .map((item) => ({
              idx: item.uniqueKey,
              sort: item.sectionNumber,
              title: item.uniqueKey,
              uniqueKey: item.uniqueKey,
              type: item.type,
              subType: item.content?.practiceType,
              layer_type: item.layer_type,
            }));
          this.tasks = res.data
            .filter((item) => item.layer_type === "popup")
            .map((item) => ({
              ...item,
              idx: item.uniqueKey,
              sort: item.sectionNumber,
              title: item.uniqueKey,
              uniqueKey: item.uniqueKey,
              type: item.type,
              subType: item.content?.practiceType,
              layer_type: item.layer_type,
            }));
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    async getChapters() {
      const barcode = this.$route.query.barcode;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/textbookContents/${barcode}`,
          loadingBar: true,
        });
        if (res.status === 200 && Array.isArray(res.data.chapters)) {
          this.chapterOptions = res.data.chapters.map((item) => ({
            value: item.chapterNumber,
            label: item.title,
          }));
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    getFullImageUrl(imageName, barcode) {
      if (!imageName) return null;
      if (typeof imageName !== "string") return null;
      if (imageName.startsWith("http")) return imageName;
      return `${this.$CONF.API_URL}/textbook/textbookContents/images/${barcode}/${imageName}`;
    },
    replaceImgSrc(html, barcode) {
      return html.replace(/<img\s+[^>]*src=["']([^"']+)["']/g, (match, src) => {
        if (src.startsWith("http")) return match;
        const newSrc = this.getFullImageUrl(src, barcode);
        return match.replace(src, newSrc);
      });
    },
    async loadSection(idx) {
      const barcode = this.$route.query.barcode;
      this.uniqueKey = idx;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/textbookContents/${barcode}/${this.chapterNumber}/${idx}`,
          loadingBar: true,
        });
        if (res.status === 200) {
          this.sectionType = res.data.type;
          this.currentSection = this.toEditableSection(res.data, barcode);
        }
      } catch (error) {
        this.$notifyVue({ type: "error", title: this.$t("alert.50") });
        console.error("Error loading section:", error);
      }
    },
    // 서버의 섹션을 편집 화면이 쓰는 형태로 바꾼다(이미지 파일명 → 전체 URL). 저장 본문도 이 형태다
    toEditableSection(sectionData, barcode) {
      if (sectionData.type === "practice") {
        const practiceType = sectionData.content?.practiceType;
        if (practiceType === "subjective") {
          const practiceSubtype = sectionData.content?.practiceSubtype;
          if (practiceSubtype === "short") {
            sectionData.content.options = sectionData.content.options.map((option) => {
              let option_text = option.option_text;
              let option_image = option.option_image;
              if (!Array.isArray(option_text)) {
                option_text = option_text == null ? [""] : [option_text];
              }
              if (!Array.isArray(option_image)) {
                option_image = option_image == null ? [null] : [option_image];
              }
              option_image = option_image.map((img) =>
                img && typeof img === "string" && !img.startsWith("http")
                  ? this.getFullImageUrl(img, barcode)
                  : img
              );
              return { ...option, option_text, option_image };
            });
          } else {
            sectionData.content.practiceImage =
              this.getFullImageUrl(sectionData.content.practiceImage, barcode) || null;
          }
        } else if (practiceType === "flashcard") {
          sectionData.content.data = sectionData.content.data.map((card) => ({
            ...card,
            front_image: this.getFullImageUrl(card.front_image, barcode),
          }));
        } else if (practiceType === "pair") {
          sectionData.content.data = sectionData.content.data.map((pair) => ({
            ...pair,
            definition_image: this.getFullImageUrl(pair.definition_image, barcode),
          }));
        } else if (practiceType === "line") {
          sectionData.content.data = sectionData.content.data.map((line) => ({
            ...line,
            line_image: this.getFullImageUrl(line.line_image, barcode),
          }));
        } else if (practiceType === "randomcard") {
          sectionData.content.data = sectionData.content.data.map((card) => ({
            ...card,
            card_image: this.getFullImageUrl(card.card_image, barcode),
          }));
        } else if (practiceType === "objective") {
          sectionData.content.options = sectionData.content.options.map((option) => ({
            ...option,
            option_image: this.getFullImageUrl(option.option_image, barcode) || null,
          }));
        } else if (practiceType === "select") {
          // select 보기는 텍스트 전용. image 서브타입의 프롬프트 삽화(문제별)만 변환
          (sectionData.content.questions || []).forEach((q) => {
            if (q.promptImage) {
              q.promptImage = this.getFullImageUrl(q.promptImage, barcode) || null;
            }
          });
        }
      } else if (sectionData.type === "text") {
        sectionData.content.text = this.replaceImgSrc(sectionData.content.text, barcode);
      }
      return sectionData;
    },
    async deleteSection(idx) {
      const barcode = this.$route.query.barcode;
      const requestFn = async () => {
        return await this.$axios({
          method: "delete",
          url: `/textbook/editor/textbookContents/${barcode}/${this.chapterNumber}/${idx}`,
        });
      };
      const result = await this.$showConfirmationAndSubmit(requestFn, null, { title: this.$t("alert.06") });
      if (result === "success") {
        this.$notifyVue({ type: "success", title: this.$t("alert.09") });
        this.chapertNumberChange(this.chapterNumber);
        this.uniqueKey = null;
        this.sectionType = "text";
        this.currentSection = this.getDefaultSection("text");
      }
    },
    // PIP 화자 이름은 녹음 파일 경로의 일부로 쓰여 공백 하나에 채점·재생이 실패한다.
    // 입력란 blur 를 거치지 않고 저장되는 경우까지 막기 위해 저장 직전에도 정규화한다.
    normalizePipSpeakerNames() {
      const content = this.currentSection?.content;
      if (!content || content.practiceType !== "pip") return;
      ["speakerA", "speakerB"].forEach((speakerType) => {
        const speaker = content[speakerType];
        if (!speaker) return;
        ["name", "nameEn"].forEach((field) => {
          if (typeof speaker[field] === "string") {
            speaker[field] = speaker[field].trim().replace(/\s+/g, " ");
          }
        });
      });
    },
    async onSubmit(values, { resetForm }) {
      const barcode = this.$route.query.barcode;
      if (barcode == null) {
        this.$notifyVue({ type: "error", title: this.$t("alert.27", { obj: this.$t("label.28") }) });
        return;
      }
      if (this.chapterNumber == null) {
        this.$notifyVue({ type: "error", title: this.$t("alert.27", { obj: this.$t("label.42") }) });
        return;
      }
      if (!this.currentSection.guideBook || this.currentSection.guideBook.length === 0) {
        delete this.currentSection.guideBook;
      }
      this.normalizePipSpeakerNames();
      const formData = new FormData();
      if (this.currentSection._audioFile instanceof File) {
        formData.append("audio", this.currentSection._audioFile);
        delete this.currentSection._audioFile;
      }
      // 메모는 빼고 보낸다. 메모는 목록의 메모 창에서만 저장하고, memo 키가 없으면 서버가 기존 메모를 둔다.
      // 패널의 사본은 목록에서 고친 뒤나 Preview 를 오간 뒤 예전 메모일 수 있다 — docs/콘텐츠_팝업섹션_메모.md §2
      const jsonData = JSON.stringify({ ...this.currentSection, memo: undefined });
      formData.append("json_data", jsonData);

      const addImages = (items, key) => {
        if (items && Array.isArray(items)) {
          items.forEach((item) => {
            if (item[key] instanceof File) {
              formData.append("images", item[key]);
            }
          });
        }
      };

      if (this.currentSection.type === "practice") {
        const practiceType = this.currentSection.content?.practiceType;
        if (practiceType === "flashcard") {
          addImages(this.currentSection.content?.data, "front_image");
        } else if (practiceType === "pair") {
          addImages(this.currentSection.content?.data, "definition_image");
        } else if (practiceType === "line") {
          addImages(this.currentSection.content?.data, "line_image");
        } else if (practiceType === "randomcard") {
          addImages(this.currentSection.content?.data, "card_image");
        } else if (practiceType === "objective") {
          addImages(this.currentSection.content?.options, "option_image");
        } else if (practiceType === "subjective") {
          const practiceSubtype = this.currentSection.content?.practiceSubtype;
          if (practiceSubtype === "short") {
            addImages(this.currentSection.content?.options, "option_image");
          } else if (practiceSubtype === "long" && this.currentSection.content.practiceImage instanceof File) {
            formData.append("images", this.currentSection.content.practiceImage);
          } else if (this.currentSection.content.image instanceof File) {
            formData.append("images", this.currentSection.content.image);
          }
        }
      }
      formData.append("layer_type", this.activeTab);

      const requestFn = async () => {
        if (!this.currentSection.uniqueKey) {
          return await this.$axios({
            method: "post",
            url: `/textbook/editor/textbookContents/${barcode}/${this.chapterNumber}/addSections`,
            data: formData,
          });
        }
        return await this.$axios({
          method: "patch",
          url: `/textbook/editor/textbookContents/${barcode}/${this.chapterNumber}/${this.uniqueKey}`,
          data: formData,
        });
      };

      const result = await this.$showConfirmationAndSubmit(requestFn, resetForm, { title: this.$t("alert.47") });
      if (result === "success") {
        this.uniqueKey = null;
        const defaultType = this.activeTab === "book" ? "text" : "practice";
        this.sectionType = defaultType;
        this.currentSection = this.getDefaultSection(defaultType, this.activeTab);
        this.chapertNumberChange(this.chapterNumber);
      }
    },
    async sectionReSorting(updatedEvents) {
      this.sections = updatedEvents;
      const barcode = this.$route.query.barcode;
      const sorting_data = updatedEvents
        .map(({ idx, sort }) => (idx !== sort ? { uniqueKey: idx, newSectionNumber: sort } : null))
        .filter(Boolean);
      const res = await this.$axios({
        method: "patch",
        url: `/textbook/editor/textbookContents/${barcode}/chapters/${this.chapterNumber}/sections/sort`,
        data: { sorting_data },
      });
      if (res.status === 200) {
        this.chapertNumberChange(this.chapterNumber);
      }
    },
    getDefaultSection(type, layerType) {
      const baseSection = { layer_type: layerType || this.activeTab };
      switch (type) {
        case "text":
          return { ...baseSection, type: "text", content: { text: "" }, guideBook: [] };
        case "practice":
          return { ...baseSection, type: "practice", content: { practiceType: "flashcard", data: [] } };
        default:
          throw new Error("Unknown section type: " + type);
      }
    },
    onSectionTypeChange(newType) {
      if (newType == null) newType = this.sectionTypes[0].value;
      this.sectionType = newType;
      this.currentSection.type = newType;
    },
    handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        this.onSubmit({}, { resetForm: () => {} });
      }
    },
    addSection() {
      this.currentSection = this.getDefaultSection(this.sectionType, this.activeTab);
    },
    async openGuideBookModal(item) {
      await this.loadSection(item.idx);
      this.showGuideBookModal = true;
    },
    closeGuideBookModal() {
      this.showGuideBookModal = false;
    },
    saveGuideBook(guideBookData) {
      this.currentSection.guideBook = guideBookData;
      this.showGuideBookModal = false;
      this.$notifyVue({ type: "info", title: "Guide Book이 설정되었습니다. 저장 버튼을 눌러 저장하세요." });
    },
    async openAudioUrlModal(item) {
      await this.loadSection(item.idx);
      this.showAudioUrlModal = true;
    },
    closeAudioUrlModal() {
      this.showAudioUrlModal = false;
    },
    saveAudioUrl(audioData) {
      if (!this.currentSection.content) this.currentSection.content = {};
      this.currentSection.content.audioUrl = audioData.audioUrl;
      this.currentSection._audioFile = audioData.audioFile;
      this.showAudioUrlModal = false;
      this.$notifyVue({ type: "info", title: "Audio URL이 설정되었습니다. 저장 버튼을 눌러 저장하세요." });
    },
    openMemoModal(item) {
      this.memoTarget = item;
      this.showMemoModal = true;
    },
    closeMemoModal() {
      this.showMemoModal = false;
    },
    // 메모는 바로 저장한다. 본문은 섹션을 새로 읽어 메모만 바꾼 전체 섹션 — 패널의 저장과 같은 형태라
    // 내용이 바뀌지 않고, 패널에서 편집 중인 섹션은 건드리지 않는다. 계약은 docs/콘텐츠_팝업섹션_메모.md
    async saveMemo(memo) {
      const { idx } = this.memoTarget;
      if (memo === (this.memoTarget.memo || "")) {
        this.showMemoModal = false;
        return;
      }
      const barcode = this.$route.query.barcode;
      this.memoSaving = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/textbookContents/${barcode}/${this.chapterNumber}/${idx}`,
        });
        const sectionData = this.toEditableSection(res.data, barcode);
        if (!sectionData.guideBook || sectionData.guideBook.length === 0) {
          delete sectionData.guideBook;
        }
        sectionData.memo = memo;
        const formData = new FormData();
        formData.append("json_data", JSON.stringify(sectionData));
        formData.append("layer_type", "popup");
        await this.$axios({
          method: "patch",
          url: `/textbook/editor/textbookContents/${barcode}/${this.chapterNumber}/${idx}`,
          data: formData,
        });
        await this.chapertNumberChange(this.chapterNumber);
        // 다시 읽은 목록에 메모가 없으면 저장됐다고 하지 않는다(서버가 memo 를 버리거나 목록을 못 읽은 경우)
        const saved = this.tasks.find((task) => task.idx === idx);
        if ((saved?.memo || "") !== memo) {
          this.$notifyVue({ type: "warn", title: "메모가 목록에 반영되지 않았습니다. 다시 확인해 주세요." });
          return;
        }
        this.showMemoModal = false;
        this.$notifyVue({ type: "success", title: memo ? "메모를 저장했습니다." : "메모를 지웠습니다." });
      } catch (error) {
        this.$notifyVue({ type: "error", title: this.$t("alert.50") });
        console.error("Error saving memo:", error);
      } finally {
        this.memoSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.sec-list {
  outline: none;

  &__h {
    margin: 0;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sub {
    margin: 0 0 18px;
    font-size: 12px;
    color: $p-ink-mid;
    font-weight: 700;
  }

  &__tabs {
    margin-bottom: 16px;
  }

  &__book {
    margin-top: 4px;
  }

  &__layout {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 18px;
    align-items: start;

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__list-wrap {
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    max-height: 700px;
    overflow-y: auto;
  }

  &__add-row {
    display: flex;
    justify-content: flex-start;
  }

  &__right {
    min-width: 0;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid $p-line;
  }
}
</style>
